/**
 * Enhanced Request Deduplicator
 * 
 * Prevents duplicate in-flight requests and caches results for short-term reuse
 */

class EnhancedRequestDeduplicator {
  constructor() {
    this.inFlightRequests = new Map();
    this.resultCache = new Map();
    this.cacheExpiry = new Map();
    this.defaultCacheTTL = 2000; // 2 seconds
  }

  /**
   * Generate cache key from request config
   */
  generateKey(config) {
    // Only deduplicate GET requests
    if (config.method && config.method.toUpperCase() !== 'GET') {
      return null;
    }

    const url = config.url || '';
    const params = config.params ? JSON.stringify(config.params) : '';
    const headers = config.headers?.Authorization ? 'auth' : 'no-auth';
    
    return `${config.method || 'GET'}:${url}:${params}:${headers}`;
  }

  /**
   * Check result cache first
   */
  getCachedResult(key) {
    if (!this.resultCache.has(key)) return null;

    const expiry = this.cacheExpiry.get(key);
    if (Date.now() > expiry) {
      this.resultCache.delete(key);
      this.cacheExpiry.delete(key);
      return null;
    }

    return this.resultCache.get(key);
  }

  /**
   * Cache successful results
   */
  setCachedResult(key, result, ttl = this.defaultCacheTTL) {
    this.resultCache.set(key, result);
    this.cacheExpiry.set(key, Date.now() + ttl);

    // Limit cache size
    if (this.resultCache.size > 100) {
      const oldestKey = this.resultCache.keys().next().value;
      this.resultCache.delete(oldestKey);
      this.cacheExpiry.delete(oldestKey);
    }
  }

  /**
   * Deduplicate request with result caching
   */
  dedupe(config, requestFn, cacheTTL = this.defaultCacheTTL) {
    const key = this.generateKey(config);

    // Non-deduplicatable requests
    if (!key) {
      return requestFn();
    }

    // Check result cache first
    const cachedResult = this.getCachedResult(key);
    if (cachedResult) {
      return Promise.resolve(cachedResult);
    }

    // Check in-flight requests
    if (this.inFlightRequests.has(key)) {
      return this.inFlightRequests.get(key);
    }

    // Create new request
    const requestPromise = requestFn()
      .then(response => {
        this.inFlightRequests.delete(key);
        // Cache the result
        this.setCachedResult(key, response, cacheTTL);
        return response;
      })
      .catch(error => {
        this.inFlightRequests.delete(key);
        throw error;
      });

    this.inFlightRequests.set(key, requestPromise);
    return requestPromise;
  }

  /**
   * Clear result cache (call on mutations)
   */
  clearResultCache(pattern = null) {
    if (!pattern) {
      this.resultCache.clear();
      this.cacheExpiry.clear();
      return;
    }

    const regex = new RegExp(pattern);
    for (const key of this.resultCache.keys()) {
      if (regex.test(key)) {
        this.resultCache.delete(key);
        this.cacheExpiry.delete(key);
      }
    }
  }

  /**
   * Clear in-flight requests (for cleanup)
   */
  clearInFlight() {
    this.inFlightRequests.clear();
  }

  /**
   * Get statistics
   */
  getStats() {
    return {
      inFlightCount: this.inFlightRequests.size,
      cachedResults: this.resultCache.size,
      cacheExpiryCount: this.cacheExpiry.size
    };
  }
}

export const requestDeduplicator = new EnhancedRequestDeduplicator();
export default requestDeduplicator;
